import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import EmptyTableBody from './EmptyTableBody';
import LoadingTableBody from './LoadingTableBody';
import Pagination from './Pagination';
import TableBody from './TableBody';
import TableHead from './TableHead';
import TableTopBar from './TableTopBar';

const Table = (props) => {
  const {
    resourceKey,
    emptyMessage,
    highlight,
    highlightType,
    fields = [],
    data = [],
    selected = [],
    config = {},
    type = '',
    responsive = false,
    onChange = _.noop,
    onRowClick = _.noop,
    onSelect = _.noop,
    onSelectAll = _.noop,
    disableRow,
    isLoading = false,
    enableSelect = false,
    withPagination,
    hasError = false,
    required = false
  } = props;
  const { page, size, sort, direction, total } = config;
  const fieldsInOrder = useMemo(() => _.orderBy(fields, (f) => f?.order), [fields]);
  const fieldsToShow = fieldsInOrder?.filter((f) => f.show);
  const topBarProps = {
    ..._.pick(props, [
      'title',
      'legend',
      'actions',
      'isLoading',
      'onFieldEnableChange',
      'onFieldOrderChange',
      'required'
    ]),
    fields: fieldsInOrder
  };
  const isEmpty = !data?.length;
  const handleChange = (change) => {
    return onChange({ ...config, ...change });
  };
  const renderTableBody = () => {
    if (isLoading) {
      return <LoadingTableBody headerLength={enableSelect ? fields.length + 1 : fields.length} />;
    }
    if (hasError) {
      return (
        <EmptyTableBody
          className="u-text-danger"
          emptyMessage={'Er is een fout opgetreden bij het ophalen van de gegevens. Probeer het later opnieuw.'}
          headerLength={enableSelect ? fields.length + 1 : fields.length}
        />
      );
    }
    if (isEmpty) {
      return (
        <EmptyTableBody
          className="u-text-light"
          emptyMessage={emptyMessage}
          headerLength={enableSelect ? fields.length + 1 : fields.length}
        />
      );
    }
    return (
      <TableBody
        resourceKey={resourceKey}
        highlight={highlight}
        highlightType={highlightType}
        fields={fieldsToShow}
        data={data}
        enableSelect={enableSelect}
        disableRow={disableRow}
        selected={selected}
        onSelect={onSelect}
        onRowClick={onRowClick}
      />
    );
  };
  const Wrapper = ({ children }) =>
    responsive ? <div className="a-table__wrapper-responsive u-margin-bottom">{children}</div> : children;

  return (
    <div className="spo-o-table u-margin-bottom-lg">
      <TableTopBar {...topBarProps} />
      <Wrapper>
        <table className={`a-table ${type ? `a-table--${type}` : ''}`}>
          <TableHead
            resourceKey={resourceKey}
            sortFunction={handleChange}
            sort={sort}
            direction={direction}
            headers={fieldsToShow}
            type={type}
            isLoading={isLoading}
            isEmpty={isEmpty}
            enableSelect={enableSelect}
            onSelectAll={onSelectAll}
            allSelected={selected?.length === data?.length}
          />
          {renderTableBody()}
        </table>
      </Wrapper>
      {withPagination && (
        <Pagination
          disabled={isLoading}
          isLoading={isLoading}
          isEmpty={isEmpty}
          onChange={handleChange}
          totalElements={total}
          pageSize={size}
          page={page}
        />
      )}
    </div>
  );
};

Table.propTypes = {
  fields: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  config: PropTypes.object.isRequired,

  actions: PropTypes.array,
  type: PropTypes.oneOf([undefined, 'primary']),
  responsive: PropTypes.bool,
  onChange: PropTypes.func,
  onFieldEnableChange: PropTypes.func,
  onFieldOrderChange: PropTypes.func,
  isLoading: PropTypes.bool
};
export default Table;
